---
title: API console
lede: Send a project's saved calls to the API your session is running, script them, chain them and document them.
order: 21
part: Check the result
---

The API console (`⌘5`) is a request client for the projects of this session that serve an API. Its calls belong to the project, not the session, so every session of that project has the same calls, sent to its own running API.

![The API console](media/api-pane.webp)

A project appears here once it says what it serves, in **Workspace settings › Repositories**.

### The bar

![The request bar](media/api-bar.webp)

One row per console, left to right:

| Control | What it does |
|---|---|
| **Environment** | Which set of values the call goes out with, or **No environment**. On a session with a [target](/targets), that target heads the list, read-only |
| Method and URL | The request. A path such as `/products` is sent to this session's port |
| **Save** | Puts the call back in the collection; live only when it changed |
| **Send via** | Runs one of the project's flows first (see [Flows](#flows)); **Send directly** clears it |
| **Send** | Sends the call |
| **Draw and note** | Marks up the response for the agent (see below) |
| **More** (`…`) | **Copy as curl** (variables resolved) and **Open in window** |

Under the bar are the request tabs **Params**, **Headers**, **Body**, **Scripts** and **Docs**, with a count on those that hold something. A body is **Raw** or a **Form** with files. The tabs are as tall as their content: drag the seam to resize, double-click it to fit the tab again.

### The left column

Four lists, one open at a time; each header has the buttons that add to it.

- **Collections** — folders of saved calls, each tagged with its method in colour. **New folder**, **New request**, and **Import and export**, which holds **Import Postman collection** and **Export as Postman**. Double-click a call to rename it. With several APIs, each is a node of this list with its own `+`, and a folder drags from one to another with its calls.
- **Flows** — chains of calls (below).
- **Environments** — named sets of values.
- **Variables** — the project's values.

### Variables and environments

On a session whose target is reached by direct access, **<target> · this session's target** heads the selector. It supplies that target's values for the project, with a secret as its placeholder, and the call still goes to the session's own ports: it opens no road to the target's hosts. On a target read through the host the entry is inert, with its reason beneath it, and the console cannot call that target.

Write `{{name}}` anywhere in a call (address, param, header, body) and it is replaced before sending. Under the address, the console shows the resolved URL and flags names nothing defines; those are sent as written.

On **Variables**, each value is a **variable** (shown as written) or a **secret** (kept in the OS keychain, shown as dots). This computer fills a secret in as the call leaves; the window never holds it.

An environment gives some variables another value: **Override** a name in it, **Reset** to fall back to the Variables page. **Send with this** makes the console use it, which is the same as picking it in the bar. **Keep here** (or **Keep on this computer**) leaves a value out of what you publish to your organization.

### Scripts

![The Scripts tab](media/api-scripts.webp)

A call carries two JavaScript scripts on its **Scripts** tab: **Pre-request**, run before it leaves, and **Post-response**, run once it answered. They use Postman's `pm` API, so a Postman collection's scripts run unchanged; `airlock` is the same object.

```js
const token = pm.response.json().accesstoken;
const payload = JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
pm.environment.set('GATEWAY_TOKEN', token);
pm.environment.set('X_PERSON', Object.keys(payload['api/accessCode'])[1]);
pm.test('signed in', () => pm.response.to.have.status(200));
```

| API | What it does |
|---|---|
| `pm.variables.set` | A value for this run only |
| `pm.environment.set` | A value kept in the environment the console sends with |
| `pm.collectionVariables.set` | Changes the variable's own value |
| `pm.request…` | In a pre-request script, change the call before it is sent (`{{names}}` still unresolved) |
| `pm.execution.skipRequest()` | Send nothing |
| `pm.test`, `pm.expect` | Check the answer; the response counts **Tests 3/4** |

`atob`, `btoa`, `Buffer`, `crypto.randomUUID()`, and `require()` of `crypto-js`, `lodash`, `uuid`, `chai` and `buffer` are available.

:::warn Scripts are sealed
A script has no network, no files and no process, and stops after two seconds. `pm.sendRequest` does not exist: put the earlier call in a flow. A secret cannot be read or changed from a script.
:::

Scripts run wherever the call is sent: **Send**, a flow, or the agent. They travel in Postman files both ways.

### The response

The status (coloured by its class), the time and the size, then **Truncated** when the body was cut, **Tests n/m** when scripts tested it, and **Script stopped** when one failed. **Body**, **Headers** and **Scripts** (what the scripts logged, tested and set) are its tabs. **Pretty** indents a JSON body. The response is shown as text, never rendered.

### Docs

![A call's Docs tab](media/api-docs.webp)

A call's **Docs** tab holds an OpenAPI 3.1 document in YAML: its path and method, what it takes and what it answers. **Write** starts one from the call's own fields, **Edit** changes it, switching between **YAML** and **Preview** as you write; the tab shows it as a **Page** or as **YAML**. A document older than OpenAPI shows **Convert to OpenAPI**, which keeps its text as the description.

On Docs, the response folds into its band; open it again from there.

### Flows

![A flow](media/api-flow.webp)

A flow is a chain of the project's calls where each step can capture values for the next. A login answers with a token; every later call uses `{{token}}`.

1. **New flow** under **Flows**.
2. **Add call** for each step, and draw arrows between them. **Add condition** adds a step that sends the run down **THEN** or **ELSE**.
3. On a step, add **Captures**: a name, and where it comes **From** — a **body** path, a **header**, or the **status**.
4. **Run flow** sends every step in order; **Stop** halts after the current one.

A capture lasts only for the run. A flow holds up to 60 steps, each with up to 8 captures.

### Noting a response

**Draw and note** on the bar works as in the [browser](/browser#drawing-on-the-page). The note carries the call as its address: the method, the URL, the status it answered with and the time it took.

### Asking the Agent

Type a sentence in the URL field (*"add the order endpoints and document them"*, *"make this flow log in first"*): **Send** becomes **Ask Agent**, and `Enter` asks it in the [Agent panel](/agent-panel) (`⌘J`). The agent reads the project and proposes changes to calls, folders, variables, environments, flows, scripts and docs. Nothing is written until you tick it, and nothing is sent.

With a flow open, the sentence is about that flow. If [Agent MCP](/agent-preview) is on, the agent can also send saved calls itself, scripts included.
